import { useState, useEffect } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Switch } from "@/components/ui/switch";
import { Calendar, Plus, Upload, X, AlertCircle, CheckSquare, Pencil, Eye, Send, CheckCircle2, Circle, Trash2, ChevronUp, ChevronDown, Archive, HelpCircle } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger, DialogClose } from "@/components/ui/dialog";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors, DragEndEvent } from '@dnd-kit/core';
import { arrayMove, SortableContext, sortableKeyboardCoordinates, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

interface Goal {
  id: string;
  title: string;
  details: string | null;
  target_amount: number;
  current_amount: number;
  target_date: string | null;
  status: string;
  priority: number;
  show_on_fundraising: boolean;
  image_url: string | null;
  submitted_by_email: string | null;
  rejection_reason: string | null;
  requester: string | null;
  allocated_funds?: number;
}

interface Topic {
  title: string;
  details: string;
}

interface Meeting {
  id: string;
  title: string;
  meeting_date: string;
  location: string | null;
  meeting_type: string | null;
  custom_meeting_type: string | null;
  topics: Topic[];
  invited_members: string[];
  status: string;
  archived_at: string | null;
  sections?: MeetingSection[];
}

interface MeetingSection {
  id: string;
  title: string;
  content: string | null;
  section_order: number;
}

interface Member {
  id: string;
  full_name: string;
  email: string;
  user_roles: { role: string }[];
}

interface Task {
  id: string;
  title: string;
  description: string | null;
  task_type: 'personal' | 'assigned' | 'general';
  assigned_to: string | null;
  status: 'pending' | 'completed';
  created_at: string;
  assigned_members?: Member[];
}

interface Alert {
  id: string;
  title: string;
  due_date: string;
  alert_date: string;
  status: string;
  created_at: string;
}

function SortableGoal({ goal, onStatusChange, onReject, onEdit, onViewDetails, onMoveUp, onMoveDown, isFirst, isLast, allGoals }: { 
  goal: Goal; 
  onStatusChange: (id: string, status: string) => void;
  onReject: (id: string, reason: string) => void;
  onEdit: (goal: Goal) => void;
  onViewDetails: (goal: Goal) => void;
  onMoveUp?: () => void;
  onMoveDown?: () => void;
  isFirst?: boolean;
  isLast?: boolean;
  allGoals?: Goal[];
}) {
  const { attributes, listeners, setNodeRef, transform, transition } = useSortable({ id: goal.id });
  const [showRejectDialog, setShowRejectDialog] = useState(false);
  const [rejectionReason, setRejectionReason] = useState("");

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  const getStatusOptions = () => {
    if (goal.status === 'submitted') return ['approved', 'rejected', 'up-next'];
    if (goal.status === 'approved') return ['up-next', 'active'];
    if (goal.status === 'up-next') return ['active', 'approved'];
    if (goal.status === 'active') return ['archive', 'up-next'];
    return [];
  };

  const handleReject = () => {
    onReject(goal.id, rejectionReason);
    setShowRejectDialog(false);
    setRejectionReason("");
  };

  const isHighestPriority = isFirst && allGoals && allGoals.filter(g => g.status === goal.status).length > 1;

  return (
    <div ref={setNodeRef} style={style} className="p-4 border border-border rounded-lg hover:shadow-card transition-shadow bg-card">
      <div className="flex items-start gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between mb-2">
            <div className="flex-1">
              <div className="flex items-center gap-2">
                <span className="font-medium block">{goal.title}</span>
                {isHighestPriority && (
                  <span className="text-xs bg-primary/10 text-primary px-2 py-0.5 rounded">Highest Priority</span>
                )}
              </div>
              {goal.requester && (
                <span className="text-xs text-muted-foreground block">Suggested by: {goal.requester}</span>
              )}
              {goal.submitted_by_email && (
                <span className="text-xs text-muted-foreground block">Submitted by: {goal.submitted_by_email}</span>
              )}
            </div>
            <div className="text-right">
              <span className="text-xs text-muted-foreground block">Target Amount:</span>
              <span className="text-sm font-semibold text-primary">£{goal.target_amount}</span>
            </div>
          </div>
          {goal.status === 'active' && (
            <div className="space-y-1 mb-2">
              <div className="flex justify-between text-xs text-muted-foreground">
                <span>Progress</span>
                <span>{Math.round((goal.current_amount / goal.target_amount) * 100)}%</span>
              </div>
              <div className="h-2 bg-secondary rounded-full overflow-hidden">
                <div 
                  className="h-full bg-primary transition-all" 
                  style={{ width: `${Math.min((goal.current_amount / goal.target_amount) * 100, 100)}%` }} 
                />
              </div>
            </div>
          )}
          <div className="flex items-center gap-2 flex-wrap">
            <div className="flex items-center gap-1">
              <Label className="text-xs text-muted-foreground">Status:</Label>
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <HelpCircle className="h-3 w-3 text-muted-foreground cursor-help" />
                  </TooltipTrigger>
                  <TooltipContent>
                    <p className="text-xs max-w-xs">
                      Change the goal's status to move it between sections: Submitted → Approved → Up Next → Active → Archive
                    </p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>
            <Select
              value={goal.status} 
              onValueChange={(val) => {
                if (val === 'rejected') {
                  setShowRejectDialog(true);
                } else {
                  onStatusChange(goal.id, val);
                }
              }}
            >
              <SelectTrigger className="w-[140px] h-8">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {getStatusOptions().map((status) => (
                  <SelectItem key={status} value={status} className="capitalize">
                    {status.replace('-', ' ')}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <div className="flex gap-2 ml-auto">
              {!isFirst && onMoveUp && (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={onMoveUp}
                  className="h-8 w-8 p-0"
                  title="Move up"
                >
                  <ChevronUp className="h-4 w-4" />
                </Button>
              )}
              {!isLast && onMoveDown && (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={onMoveDown}
                  className="h-8 w-8 p-0"
                  title="Move down"
                >
                  <ChevronDown className="h-4 w-4" />
                </Button>
              )}
              <Button size="sm" variant="outline" onClick={() => onEdit(goal)}>
                <Pencil className="h-3 w-3 mr-1" />
                Edit
              </Button>
              <Button size="sm" variant="outline" onClick={() => onViewDetails(goal)}>
                <Eye className="h-3 w-3 mr-1" />
                Details
              </Button>
            </div>
            {!goal.show_on_fundraising && (
              <span className="text-xs text-muted-foreground">(Hidden from parents)</span>
            )}
          </div>
        </div>
      </div>

      <Dialog open={showRejectDialog} onOpenChange={setShowRejectDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Reject Goal</DialogTitle>
            <DialogDescription>
              Please provide a reason for rejecting this goal. The submitter will be notified.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <Textarea
              placeholder="Reason for rejection..."
              value={rejectionReason}
              onChange={(e) => setRejectionReason(e.target.value)}
              rows={4}
            />
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setShowRejectDialog(false)}>Cancel</Button>
              <Button variant="destructive" onClick={handleReject}>Reject Goal</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default function ChairDashboard() {
  const { toast } = useToast();
  const [isGoalDialogOpen, setIsGoalDialogOpen] = useState(false);
  const [isMeetingDialogOpen, setIsMeetingDialogOpen] = useState(false);
  const [isEditGoalDialogOpen, setIsEditGoalDialogOpen] = useState(false);
  const [isViewGoalDialogOpen, setIsViewGoalDialogOpen] = useState(false);
  const [selectedGoal, setSelectedGoal] = useState<Goal | null>(null);
  const [goals, setGoals] = useState<Goal[]>([]);
  const [meetings, setMeetings] = useState<Meeting[]>([]);
  const [members, setMembers] = useState<Member[]>([]);
  const [tasks, setTasks] = useState<Task[]>([]);
  const [alerts, setAlerts] = useState<Alert[]>([]);
  const [selectedMeeting, setSelectedMeeting] = useState<Meeting | null>(null);
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [showOnFundraising, setShowOnFundraising] = useState(true);
  const [meetingType, setMeetingType] = useState("");
  const [customMeetingType, setCustomMeetingType] = useState("");
  const [topics, setTopics] = useState<Topic[]>([{ title: "", details: "" }]);
  const [selectedMembers, setSelectedMembers] = useState<string[]>([]);
  const [newTaskTitle, setNewTaskTitle] = useState("");
  const [newTaskDescription, setNewTaskDescription] = useState("");
  const [taskType, setTaskType] = useState<'general' | 'assigned'>('general');
  const [taskAssignees, setTaskAssignees] = useState<string[]>([]);
  const [commMessage, setCommMessage] = useState("");
  const [commRecipients, setCommRecipients] = useState<string[]>([]);
  const [showArchivedGoals, setShowArchivedGoals] = useState(false);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  useEffect(() => {
    fetchGoals();
    fetchMeetings();
    fetchMembers();
    fetchTasks();
    fetchAlerts();
    
    // Update meeting statuses based on date
    const interval = setInterval(() => {
      updateMeetingStatuses();
    }, 60000); // Check every minute
    
    return () => clearInterval(interval);
  }, []);

  const updateMeetingStatuses = async () => {
    const now = new Date();
    const updates = meetings
      .filter(m => new Date(m.meeting_date) < now && m.status === 'upcoming')
      .map(m => 
        supabase
          .from('meetings')
          .update({ status: 'taken-place' })
          .eq('id', m.id)
      );
    
    if (updates.length > 0) {
      await Promise.all(updates);
      fetchMeetings();
    }
  };

  const fetchGoals = async () => {
    const { data, error } = await supabase
      .from('goals')
      .select('*')
      .order('priority', { ascending: true });
    
    if (!error && data) {
      // Fetch allocations for each goal
      const { data: allocations } = await supabase
        .from('fund_allocations')
        .select('goal_id, amount');
      
      const allocationMap = new Map();
      allocations?.forEach(a => {
        const current = allocationMap.get(a.goal_id) || 0;
        allocationMap.set(a.goal_id, current + Number(a.amount));
      });
      
      const enrichedGoals = data.map(g => ({
        ...g,
        allocated_funds: allocationMap.get(g.id) || 0
      }));
      
      setGoals(enrichedGoals);
    }
  };

  const fetchMeetings = async () => {
    const { data, error } = await supabase
      .from('meetings')
      .select(`
        *,
        sections:meeting_sections(*)
      `)
      .is('archived_at', null)
      .order('meeting_date', { ascending: false });
    
    if (!error && data) setMeetings(data as any);
  };

  const fetchMembers = async () => {
    const { data: membersData } = await supabase
      .from('members')
      .select('id, full_name, email')
      .eq('status', 'active');

    if (membersData) {
      const membersWithRoles = await Promise.all(
        membersData.map(async (m) => {
          const { data: roles } = await supabase
            .from('user_roles')
            .select('role')
            .eq('user_id', m.id);
          return { ...m, user_roles: roles || [] };
        })
      );
      setMembers(membersWithRoles);
    }
  };

  const fetchTasks = async () => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return;

    const { data, error } = await supabase
      .from('tasks')
      .select('*')
      .or(`assigned_to.eq.${user.id},created_by.eq.${user.id},task_type.eq.general`)
      .order('created_at', { ascending: false });

    if (!error && data) setTasks(data as any);
  };

  const fetchAlerts = async () => {
    const { data, error } = await supabase
      .from('alerts')
      .select('*')
      .eq('status', 'active')
      .order('alert_date', { ascending: true });
    
    if (!error && data) setAlerts(data);
  };

  const handleCreateGoal = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const formData = new FormData(e.currentTarget);
    
    let imageUrl = null;
    if (imageFile) {
      const fileName = `${Date.now()}_${imageFile.name}`;
      const { data, error: uploadError } = await supabase.storage
        .from('fundraising-images')
        .upload(fileName, imageFile);

      if (!uploadError && data) {
        const { data: { publicUrl } } = supabase.storage
          .from('fundraising-images')
          .getPublicUrl(data.path);
        imageUrl = publicUrl;
      }
    }

    const { error } = await supabase.from('goals').insert({
      title: formData.get('title') as string,
      requester: formData.get('requester') as string || null,
      details: formData.get('details') as string,
      target_amount: parseFloat(formData.get('target_amount') as string),
      target_date: formData.get('target_date') as string || null,
      show_on_fundraising: showOnFundraising,
      image_url: imageUrl,
      status: 'submitted',
      priority: goals.length,
    });

    if (error) {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    } else {
      toast({
        title: "Goal created!",
        description: "Your fundraising goal has been added successfully.",
      });
      setIsGoalDialogOpen(false);
      setImageFile(null);
      setShowOnFundraising(true);
      fetchGoals();
    }
  };

  const handleCreateMeeting = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const formData = new FormData(e.currentTarget);
    
    const { data: { user } } = await supabase.auth.getUser();
    
    const { error } = await supabase.from('meetings').insert({
      title: formData.get('title') as string,
      meeting_date: formData.get('meeting_date') as string,
      location: formData.get('location') as string,
      meeting_type: meetingType === 'Other' ? null : meetingType,
      custom_meeting_type: meetingType === 'Other' ? customMeetingType : null,
      topics: topics.filter(t => t.title && t.details) as any,
      invited_members: selectedMembers,
      status: 'upcoming',
      created_by: user?.id,
    } as any);

    if (error) {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    } else {
      toast({
        title: "Meeting created",
        description: "Meeting details have been saved and members will be notified.",
      });
      setIsMeetingDialogOpen(false);
      setMeetingType("");
      setCustomMeetingType("");
      setTopics([{ title: "", details: "" }]);
      setSelectedMembers([]);
      fetchMeetings();
    }
  };

  const handleGoalStatusChange = async (goalId: string, newStatus: string) => {
    const goal = goals.find(g => g.id === goalId);
    if (!goal) return;

    // Optimistic update - update local state immediately
    setGoals(prevGoals => 
      prevGoals.map(g => g.id === goalId ? { ...g, status: newStatus } : g)
    );

    const { error } = await supabase
      .from('goals')
      .update({ status: newStatus })
      .eq('id', goalId);

    if (error) {
      // Revert on error
      setGoals(prevGoals => 
        prevGoals.map(g => g.id === goalId ? { ...g, status: goal.status } : g)
      );
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    } else {
      if (goal.submitted_by_email && newStatus !== 'submitted') {
        toast({
          title: "Status updated",
          description: `Goal moved to ${newStatus.replace('-', ' ')} section. Email sent to ${goal.submitted_by_email}`,
        });
      } else {
        toast({
          title: "Status updated",
          description: `Goal moved to ${newStatus.replace('-', ' ')} section.`,
        });
      }
      // Refetch to ensure consistency
      await fetchGoals();
    }
  };

  const handleGoalReject = async (goalId: string, reason: string) => {
    const goal = goals.find(g => g.id === goalId);
    if (!goal) return;

    const { error } = await supabase
      .from('goals')
      .update({ 
        status: 'rejected',
        rejection_reason: reason 
      })
      .eq('id', goalId);

    if (error) {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    } else {
      if (goal.submitted_by_email) {
        toast({
          title: "Goal rejected",
          description: `Email notification sent to ${goal.submitted_by_email} with reason`,
        });
      }
      fetchGoals();
    }
  };

  const handleDragEnd = async (event: DragEndEvent, status: string) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const statusGoals = goals.filter(g => g.status === status);
    const oldIndex = statusGoals.findIndex((g) => g.id === active.id);
    const newIndex = statusGoals.findIndex((g) => g.id === over.id);

    const newOrder = arrayMove(statusGoals, oldIndex, newIndex);
    
    // Update priorities in database
    const updates = newOrder.map((goal, index) => 
      supabase
        .from('goals')
        .update({ priority: index })
        .eq('id', goal.id)
    );

    await Promise.all(updates);
    
    toast({
      title: "Priority updated",
      description: "Goal priority has been reordered and saved.",
    });
    
    fetchGoals();
  };

  const handleArchiveMeeting = async (meetingId: string) => {
    const meeting = meetings.find(m => m.id === meetingId);
    if (!meeting) return;

    const hasMinutes = meeting.sections && meeting.sections.length > 0;
    
    if (!hasMinutes) {
      const confirmed = window.confirm("No minutes have been added to this meeting. Are you sure you want to archive it?");
      if (!confirmed) return;
    }

    const { error } = await supabase
      .from('meetings')
      .update({ archived_at: new Date().toISOString() })
      .eq('id', meetingId);

    if (error) {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    } else {
      toast({
        title: "Meeting archived",
        description: "The meeting has been moved to archived meetings.",
      });
      fetchMeetings();
    }
  };

  const handleAddTask = async (e: React.FormEvent) => {
    e.preventDefault();
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return;

    if (taskType === 'assigned' && taskAssignees.length === 0) {
      toast({
        title: "Error",
        description: "Please select at least one member to assign the task to.",
        variant: "destructive",
      });
      return;
    }

    // Create tasks for each assignee if assigned type
    if (taskType === 'assigned') {
      const tasksToInsert = taskAssignees.map(assigneeId => ({
        title: newTaskTitle,
        description: newTaskDescription || null,
        task_type: 'assigned',
        assigned_to: assigneeId === 'me' ? user.id : assigneeId,
        created_by: user.id,
        status: 'pending',
      }));

      const { error } = await supabase.from('tasks').insert(tasksToInsert);
      
      if (error) {
        toast({
          title: "Error",
          description: error.message,
          variant: "destructive",
        });
        return;
      }
    } else {
      // General task
      const { error } = await supabase.from('tasks').insert({
        title: newTaskTitle,
        description: newTaskDescription || null,
        task_type: 'general',
        created_by: user.id,
        status: 'pending',
      });

      if (error) {
        toast({
          title: "Error",
          description: error.message,
          variant: "destructive",
        });
        return;
      }
    }

    toast({
      title: "Task added",
      description: taskType === 'assigned' ? "Task has been assigned successfully." : "General task has been created.",
    });
    
    setNewTaskTitle("");
    setNewTaskDescription("");
    setTaskAssignees([]);
    fetchTasks();
  };

  const handleToggleTask = async (taskId: string, currentStatus: string) => {
    const newStatus = currentStatus === 'completed' ? 'pending' : 'completed';
    const { error } = await supabase
      .from('tasks')
      .update({ 
        status: newStatus,
        completed_at: newStatus === 'completed' ? new Date().toISOString() : null 
      })
      .eq('id', taskId);

    if (!error) {
      fetchTasks();
    }
  };

  const handleDeleteTask = async (taskId: string) => {
    const { error } = await supabase
      .from('tasks')
      .delete()
      .eq('id', taskId);

    if (!error) {
      fetchTasks();
    }
  };

  const handleSendCommunication = async () => {
    if (!commMessage || commRecipients.length === 0) {
      toast({
        title: "Error",
        description: "Please enter a message and select at least one recipient.",
        variant: "destructive",
      });
      return;
    }

    // Here you would typically send emails via an edge function
    toast({
      title: "Communication sent",
      description: `Message sent to ${commRecipients.length} member(s).`,
    });
    
    setCommMessage("");
    setCommRecipients([]);
  };

  const handleMoveGoalUp = async (goal: Goal) => {
    const statusGoals = goals.filter(g => g.status === goal.status).sort((a, b) => a.priority - b.priority);
    const index = statusGoals.findIndex(g => g.id === goal.id);
    if (index <= 0) return;

    const goalAbove = statusGoals[index - 1];
    await supabase.from('goals').update({ priority: goalAbove.priority }).eq('id', goal.id);
    await supabase.from('goals').update({ priority: goal.priority }).eq('id', goalAbove.id);
    fetchGoals();
  };

  const handleMoveGoalDown = async (goal: Goal) => {
    const statusGoals = goals.filter(g => g.status === goal.status).sort((a, b) => a.priority - b.priority);
    const index = statusGoals.findIndex(g => g.id === goal.id);
    if (index === -1 || index >= statusGoals.length - 1) return;

    const goalBelow = statusGoals[index + 1];
    await supabase.from('goals').update({ priority: goalBelow.priority }).eq('id', goal.id);
    await supabase.from('goals').update({ priority: goal.priority }).eq('id', goalBelow.id);
    fetchGoals();
  };

  const activeGoals = goals.filter(g => g.status === 'active');
  const upNextGoals = goals.filter(g => g.status === 'up-next');
  const approvedGoals = goals.filter(g => g.status === 'approved');
  const submittedGoals = goals.filter(g => g.status === 'submitted');
  const archivedGoals = goals.filter(g => g.status === 'archive');

  const personalTasks = tasks.filter(t => t.task_type === 'personal' || t.task_type === 'assigned');
  const generalTasks = tasks.filter(t => t.task_type === 'general');

  return (
    <div className="min-h-screen bg-background">
      <div className="container mx-auto p-4 space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-4xl font-bold bg-gradient-to-r from-primary to-primary/60 bg-clip-text text-transparent">
              Chair Dashboard
            </h1>
            <p className="text-muted-foreground mt-2">Manage goals, meetings, and communications</p>
          </div>
        </div>

        <Tabs defaultValue="goals" className="space-y-4">
          <TabsList className="inline-flex h-auto items-center justify-start gap-8 border-b border-border w-full">
            <TabsTrigger value="goals" className="inline-flex items-center justify-center whitespace-nowrap pb-3 px-0 text-sm font-medium text-muted-foreground transition-all border-b-2 border-transparent data-[state=active]:text-primary data-[state=active]:border-primary hover:text-foreground">Goals</TabsTrigger>
            <TabsTrigger value="meetings" className="inline-flex items-center justify-center whitespace-nowrap pb-3 px-0 text-sm font-medium text-muted-foreground transition-all border-b-2 border-transparent data-[state=active]:text-primary data-[state=active]:border-primary hover:text-foreground">Meetings</TabsTrigger>
            <TabsTrigger value="tasks" className="inline-flex items-center justify-center whitespace-nowrap pb-3 px-0 text-sm font-medium text-muted-foreground transition-all border-b-2 border-transparent data-[state=active]:text-primary data-[state=active]:border-primary hover:text-foreground">Tasks</TabsTrigger>
            <TabsTrigger value="communications" className="inline-flex items-center justify-center whitespace-nowrap pb-3 px-0 text-sm font-medium text-muted-foreground transition-all border-b-2 border-transparent data-[state=active]:text-primary data-[state=active]:border-primary hover:text-foreground">Communications</TabsTrigger>
            <TabsTrigger value="alerts" className="inline-flex items-center justify-center whitespace-nowrap pb-3 px-0 text-sm font-medium text-muted-foreground transition-all border-b-2 border-transparent data-[state=active]:text-primary data-[state=active]:border-primary hover:text-foreground">Alerts</TabsTrigger>
          </TabsList>

          {/* Goals Tab */}
          <TabsContent value="goals" className="space-y-4">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle>Manage Fundraising Goals</CardTitle>
                    <CardDescription>Manage fundraising goals and their status.</CardDescription>
                    <p className="text-sm text-muted-foreground mt-2">
                      Use the up/down arrows to adjust goal priority within each section.
                    </p>
                  </div>
                  <div className="flex gap-2">
                    <Button variant="outline" onClick={() => setShowArchivedGoals(!showArchivedGoals)}>
                      <Archive className="h-4 w-4 mr-2" />
                      {showArchivedGoals ? 'Hide' : 'View'} Archived
                    </Button>
                    <Dialog open={isGoalDialogOpen} onOpenChange={setIsGoalDialogOpen}>
                      <DialogTrigger asChild>
                        <Button>
                          <Plus className="h-4 w-4 mr-2" />
                          Add Goal
                        </Button>
                      </DialogTrigger>
                      <DialogContent className="max-w-md">
                      <DialogHeader>
                        <DialogTitle>Create New Goal</DialogTitle>
                        <DialogDescription>Add a new fundraising goal</DialogDescription>
                      </DialogHeader>
                      <form onSubmit={handleCreateGoal} className="space-y-4">
                        <div className="space-y-2">
                          <Label htmlFor="title">Goal Title</Label>
                          <Input id="title" name="title" placeholder="e.g., New Playground Equipment" required />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="requester">Requester (Optional)</Label>
                          <Input id="requester" name="requester" placeholder="Person who suggested this idea" />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="target_amount">Target Amount (£)</Label>
                          <Input id="target_amount" name="target_amount" type="number" step="0.01" required />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="target_date">Target Date (Optional)</Label>
                          <Input id="target_date" name="target_date" type="date" />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="details">Details</Label>
                          <Textarea id="details" name="details" rows={3} placeholder="Describe the goal..." />
                        </div>
                        <div className="space-y-2">
                          <Label>Image (Optional)</Label>
                          <div
                            className={`border-2 border-dashed rounded-lg p-6 text-center ${
                              isDragging ? 'border-primary bg-primary/10' : 'border-border'
                            }`}
                            onDragOver={(e) => {
                              e.preventDefault();
                              setIsDragging(true);
                            }}
                            onDragLeave={() => setIsDragging(false)}
                            onDrop={(e) => {
                              e.preventDefault();
                              setIsDragging(false);
                              const file = e.dataTransfer.files[0];
                              if (file && file.type.startsWith('image/')) {
                                setImageFile(file);
                              }
                            }}
                          >
                            <input
                              type="file"
                              id="image-upload"
                              accept="image/*"
                              className="hidden"
                              onChange={(e) => {
                                const file = e.target.files?.[0];
                                if (file) setImageFile(file);
                              }}
                            />
                            <label htmlFor="image-upload" className="cursor-pointer">
                              {imageFile ? (
                                <div className="space-y-2">
                                  <p className="text-sm font-medium">{imageFile.name}</p>
                                  <Button type="button" variant="outline" size="sm" onClick={() => setImageFile(null)}>
                                    <X className="h-4 w-4 mr-1" />
                                    Remove
                                  </Button>
                                </div>
                              ) : (
                                <div className="space-y-2">
                                  <Upload className="h-8 w-8 mx-auto text-muted-foreground" />
                                  <p className="text-sm text-muted-foreground">
                                    Drag and drop or click to upload
                                  </p>
                                </div>
                              )}
                            </label>
                          </div>
                        </div>
                        <div className="flex items-center space-x-2">
                          <Switch
                            id="show-on-fundraising"
                            checked={showOnFundraising}
                            onCheckedChange={setShowOnFundraising}
                          />
                          <Label htmlFor="show-on-fundraising">Show on fundraising page</Label>
                        </div>
                        <div className="flex justify-end gap-2">
                          <DialogClose asChild>
                            <Button type="button" variant="outline">Cancel</Button>
                          </DialogClose>
                          <Button type="submit">Create Goal</Button>
                        </div>
                      </form>
                    </DialogContent>
                  </Dialog>
                </div>
              </div>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Active Goals */}
                <div>
                  <h3 className="font-semibold text-lg mb-3 flex items-center gap-2">
                    <CheckCircle2 className="h-5 w-5 text-green-600" />
                    Active Goals
                  </h3>
                  <DndContext
                    sensors={sensors}
                    collisionDetection={closestCenter}
                    onDragEnd={(e) => handleDragEnd(e, 'active')}
                  >
                    <SortableContext items={activeGoals.map(g => g.id)} strategy={verticalListSortingStrategy}>
                      <div className="space-y-2">
                        {activeGoals.length === 0 ? (
                          <p className="text-sm text-muted-foreground">No active goals</p>
                        ) : (
                          activeGoals.map((goal, idx) => (
                            <SortableGoal
                              key={goal.id}
                              goal={goal}
                              onStatusChange={handleGoalStatusChange}
                              onReject={handleGoalReject}
                              onEdit={(g) => {
                                setSelectedGoal(g);
                                setIsEditGoalDialogOpen(true);
                              }}
                              onViewDetails={(g) => {
                                setSelectedGoal(g);
                                setIsViewGoalDialogOpen(true);
                              }}
                              onMoveUp={() => handleMoveGoalUp(goal)}
                              onMoveDown={() => handleMoveGoalDown(goal)}
                              isFirst={idx === 0}
                              isLast={idx === activeGoals.length - 1}
                              allGoals={activeGoals}
                            />
                          ))
                        )}
                      </div>
                    </SortableContext>
                  </DndContext>
                </div>

                {/* Up Next Goals */}
                <div>
                  <h3 className="font-semibold text-lg mb-3 flex items-center gap-2">
                    <AlertCircle className="h-5 w-5 text-blue-600" />
                    Up Next Goals
                  </h3>
                  <DndContext
                    sensors={sensors}
                    collisionDetection={closestCenter}
                    onDragEnd={(e) => handleDragEnd(e, 'up-next')}
                  >
                    <SortableContext items={upNextGoals.map(g => g.id)} strategy={verticalListSortingStrategy}>
                      <div className="space-y-2">
                        {upNextGoals.length === 0 ? (
                          <p className="text-sm text-muted-foreground">No up next goals</p>
                        ) : (
                          upNextGoals.map((goal, idx) => (
                            <SortableGoal
                              key={goal.id}
                              goal={goal}
                              onStatusChange={handleGoalStatusChange}
                              onReject={handleGoalReject}
                              onEdit={(g) => {
                                setSelectedGoal(g);
                                setIsEditGoalDialogOpen(true);
                              }}
                              onViewDetails={(g) => {
                                setSelectedGoal(g);
                                setIsViewGoalDialogOpen(true);
                              }}
                              onMoveUp={() => handleMoveGoalUp(goal)}
                              onMoveDown={() => handleMoveGoalDown(goal)}
                              isFirst={idx === 0}
                              isLast={idx === upNextGoals.length - 1}
                              allGoals={upNextGoals}
                            />
                          ))
                        )}
                      </div>
                    </SortableContext>
                  </DndContext>
                </div>

                {/* Approved Goals */}
                <div>
                  <h3 className="font-semibold text-lg mb-3 flex items-center gap-2">
                    <CheckSquare className="h-5 w-5 text-purple-600" />
                    Approved Goals
                  </h3>
                  <DndContext
                    sensors={sensors}
                    collisionDetection={closestCenter}
                    onDragEnd={(e) => handleDragEnd(e, 'approved')}
                  >
                    <SortableContext items={approvedGoals.map(g => g.id)} strategy={verticalListSortingStrategy}>
                      <div className="space-y-2">
                        {approvedGoals.length === 0 ? (
                          <p className="text-sm text-muted-foreground">No approved goals</p>
                        ) : (
                          approvedGoals.map((goal, idx) => (
                            <SortableGoal
                              key={goal.id}
                              goal={goal}
                              onStatusChange={handleGoalStatusChange}
                              onReject={handleGoalReject}
                              onEdit={(g) => {
                                setSelectedGoal(g);
                                setIsEditGoalDialogOpen(true);
                              }}
                              onViewDetails={(g) => {
                                setSelectedGoal(g);
                                setIsViewGoalDialogOpen(true);
                              }}
                              onMoveUp={() => handleMoveGoalUp(goal)}
                              onMoveDown={() => handleMoveGoalDown(goal)}
                              isFirst={idx === 0}
                              isLast={idx === approvedGoals.length - 1}
                              allGoals={approvedGoals}
                            />
                          ))
                        )}
                      </div>
                    </SortableContext>
                  </DndContext>
                </div>

                {/* Submitted Goals */}
                <div>
                  <h3 className="font-semibold text-lg mb-3 flex items-center gap-2">
                    <Calendar className="h-5 w-5 text-orange-600" />
                    Submitted Goals
                  </h3>
                  <DndContext
                    sensors={sensors}
                    collisionDetection={closestCenter}
                    onDragEnd={(e) => handleDragEnd(e, 'submitted')}
                  >
                    <SortableContext items={submittedGoals.map(g => g.id)} strategy={verticalListSortingStrategy}>
                      <div className="space-y-2">
                        {submittedGoals.length === 0 ? (
                          <p className="text-sm text-muted-foreground">No submitted goals</p>
                        ) : (
                          submittedGoals.map((goal, idx) => (
                            <SortableGoal
                              key={goal.id}
                              goal={goal}
                              onStatusChange={handleGoalStatusChange}
                              onReject={handleGoalReject}
                              onEdit={(g) => {
                                setSelectedGoal(g);
                                setIsEditGoalDialogOpen(true);
                              }}
                              onViewDetails={(g) => {
                                setSelectedGoal(g);
                                setIsViewGoalDialogOpen(true);
                              }}
                              onMoveUp={() => handleMoveGoalUp(goal)}
                              onMoveDown={() => handleMoveGoalDown(goal)}
                              isFirst={idx === 0}
                              isLast={idx === submittedGoals.length - 1}
                              allGoals={submittedGoals}
                            />
                          ))
                        )}
                      </div>
                    </SortableContext>
                  </DndContext>
                </div>

                {/* Archived Goals */}
                {showArchivedGoals && (
                  <div>
                    <h3 className="font-semibold text-lg mb-3 flex items-center gap-2">
                      <Archive className="h-5 w-5 text-gray-600" />
                      Archived Goals
                    </h3>
                    <div className="space-y-2">
                      {archivedGoals.length === 0 ? (
                        <p className="text-sm text-muted-foreground">No archived goals</p>
                      ) : (
                        archivedGoals.map((goal, idx) => (
                          <SortableGoal
                            key={goal.id}
                            goal={goal}
                            onStatusChange={handleGoalStatusChange}
                            onReject={handleGoalReject}
                            onEdit={(g) => {
                              setSelectedGoal(g);
                              setIsEditGoalDialogOpen(true);
                            }}
                            onViewDetails={(g) => {
                              setSelectedGoal(g);
                              setIsViewGoalDialogOpen(true);
                            }}
                            onMoveUp={() => handleMoveGoalUp(goal)}
                            onMoveDown={() => handleMoveGoalDown(goal)}
                            isFirst={idx === 0}
                            isLast={idx === archivedGoals.length - 1}
                            allGoals={archivedGoals}
                          />
                        ))
                      )}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* View Goal Details Dialog */}
            <Dialog open={isViewGoalDialogOpen} onOpenChange={setIsViewGoalDialogOpen}>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Goal Details</DialogTitle>
                </DialogHeader>
                {selectedGoal && (
                  <div className="space-y-4">
                    {selectedGoal.image_url && (
                      <img src={selectedGoal.image_url} alt={selectedGoal.title} className="w-full rounded-lg" />
                    )}
                    <div>
                      <h3 className="font-semibold">{selectedGoal.title}</h3>
                      {selectedGoal.requester && (
                        <p className="text-sm text-muted-foreground">Suggested by: {selectedGoal.requester}</p>
                      )}
                    </div>
                    <div className="space-y-2">
                      <p><span className="font-medium">Target Amount:</span> £{selectedGoal.target_amount}</p>
                      <p><span className="font-medium">Current Amount:</span> £{selectedGoal.current_amount}</p>
                      {selectedGoal.target_date && (
                        <p><span className="font-medium">Target Date:</span> {new Date(selectedGoal.target_date).toLocaleDateString()}</p>
                      )}
                      <p><span className="font-medium">Status:</span> {selectedGoal.status}</p>
                    </div>
                    {selectedGoal.details && (
                      <div>
                        <p className="font-medium mb-1">Details:</p>
                        <p className="text-sm">{selectedGoal.details}</p>
                      </div>
                    )}
                    <Button onClick={() => setIsViewGoalDialogOpen(false)}>Close</Button>
                  </div>
                )}
              </DialogContent>
            </Dialog>

            {/* Edit Goal Dialog */}
            <Dialog open={isEditGoalDialogOpen} onOpenChange={setIsEditGoalDialogOpen}>
              <DialogContent className="max-w-md">
                <DialogHeader>
                  <DialogTitle>Edit Goal</DialogTitle>
                  <DialogDescription>Update goal details and settings</DialogDescription>
                </DialogHeader>
                {selectedGoal && (
                  <form onSubmit={async (e) => {
                    e.preventDefault();
                    const formData = new FormData(e.currentTarget);
                    
                    const { error } = await supabase
                      .from('goals')
                      .update({
                        title: formData.get('title') as string,
                        requester: formData.get('requester') as string || null,
                        target_amount: parseFloat(formData.get('target_amount') as string),
                        target_date: formData.get('target_date') as string || null,
                        details: formData.get('details') as string || null,
                        show_on_fundraising: formData.get('show_on_fundraising') === 'on',
                      })
                      .eq('id', selectedGoal.id);

                    if (error) {
                      toast({
                        title: "Error",
                        description: error.message,
                        variant: "destructive",
                      });
                    } else {
                      toast({
                        title: "Goal updated",
                        description: "The goal has been updated successfully.",
                      });
                      setIsEditGoalDialogOpen(false);
                      setSelectedGoal(null);
                      fetchGoals();
                    }
                  }} className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="edit-title">Goal Title</Label>
                      <Input id="edit-title" name="title" defaultValue={selectedGoal.title} required />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="edit-requester">Requester (Optional)</Label>
                      <Input id="edit-requester" name="requester" defaultValue={selectedGoal.requester || ''} />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="edit-target-amount">Target Amount (£)</Label>
                      <Input id="edit-target-amount" name="target_amount" type="number" step="0.01" defaultValue={selectedGoal.target_amount} required />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="edit-target-date">Target Date (Optional)</Label>
                      <Input id="edit-target-date" name="target_date" type="date" defaultValue={selectedGoal.target_date || ''} />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="edit-details">Details</Label>
                      <Textarea id="edit-details" name="details" rows={3} defaultValue={selectedGoal.details || ''} />
                    </div>
                    <div className="flex items-center space-x-2">
                      <Checkbox id="edit-show-on-fundraising" name="show_on_fundraising" defaultChecked={selectedGoal.show_on_fundraising} />
                      <Label htmlFor="edit-show-on-fundraising">Show on fundraising page</Label>
                    </div>
                    <div className="flex justify-end gap-2">
                      <Button type="button" variant="outline" onClick={() => {
                        setIsEditGoalDialogOpen(false);
                        setSelectedGoal(null);
                      }}>Cancel</Button>
                      <Button type="submit">Update Goal</Button>
                    </div>
                  </form>
                )}
              </DialogContent>
            </Dialog>
          </TabsContent>

          {/* Meetings Tab */}
          <TabsContent value="meetings" className="space-y-4">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle>Meetings</CardTitle>
                    <CardDescription>Schedule and manage committee meetings</CardDescription>
                  </div>
                  <Dialog open={isMeetingDialogOpen} onOpenChange={setIsMeetingDialogOpen}>
                    <DialogTrigger asChild>
                      <Button>
                        <Plus className="h-4 w-4 mr-2" />
                        Add Meeting
                      </Button>
                    </DialogTrigger>
                    <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
                      <DialogHeader>
                        <DialogTitle>Create New Meeting</DialogTitle>
                        <DialogDescription>Schedule a committee meeting</DialogDescription>
                      </DialogHeader>
                      <form onSubmit={handleCreateMeeting} className="space-y-4">
                        <div className="space-y-2">
                          <Label htmlFor="meeting_title">Meeting Title</Label>
                          <Input id="meeting_title" name="title" required />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="meeting_type">Meeting Type</Label>
                          <Select value={meetingType} onValueChange={setMeetingType} required>
                            <SelectTrigger>
                              <SelectValue placeholder="Select meeting type" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="AGM">AGM</SelectItem>
                              <SelectItem value="EGM">EGM</SelectItem>
                              <SelectItem value="Committee">Committee</SelectItem>
                              <SelectItem value="Event Planning">Event Planning</SelectItem>
                              <SelectItem value="Other">Other</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        {meetingType === 'Other' && (
                          <div className="space-y-2">
                            <Label htmlFor="custom_type">Custom Meeting Type</Label>
                            <Input
                              id="custom_type"
                              value={customMeetingType}
                              onChange={(e) => setCustomMeetingType(e.target.value)}
                              placeholder="Enter custom meeting type"
                              required
                            />
                          </div>
                        )}
                        <div className="space-y-2">
                          <Label htmlFor="meeting_date">Meeting Date & Time</Label>
                          <Input id="meeting_date" name="meeting_date" type="datetime-local" required />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="location">Location</Label>
                          <Input id="location" name="location" />
                        </div>
                        <div className="space-y-2">
                          <Label>Topics</Label>
                          {topics.map((topic, index) => (
                            <div key={index} className="space-y-2 p-3 border rounded">
                              <Input
                                placeholder="Topic title"
                                value={topic.title}
                                onChange={(e) => {
                                  const newTopics = [...topics];
                                  newTopics[index].title = e.target.value;
                                  setTopics(newTopics);
                                }}
                              />
                              <Textarea
                                placeholder="Topic details"
                                value={topic.details}
                                onChange={(e) => {
                                  const newTopics = [...topics];
                                  newTopics[index].details = e.target.value;
                                  setTopics(newTopics);
                                }}
                                rows={2}
                              />
                              {topics.length > 1 && (
                                <Button
                                  type="button"
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => {
                                    const newTopics = topics.filter((_, i) => i !== index);
                                    setTopics(newTopics);
                                  }}
                                >
                                  <X className="h-4 w-4" />
                                </Button>
                              )}
                            </div>
                          ))}
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={() => setTopics([...topics, { title: "", details: "" }])}
                          >
                            <Plus className="h-4 w-4 mr-1" />
                            Add Topic
                          </Button>
                        </div>
                        <div className="space-y-2">
                          <Label>Invite Members</Label>
                          <div className="grid grid-cols-2 gap-2 max-h-40 overflow-y-auto border rounded p-2">
                            {members.map((member) => (
                              <div key={member.id} className="flex items-center space-x-2">
                                <Checkbox
                                  id={`member-${member.id}`}
                                  checked={selectedMembers.includes(member.id)}
                                  onCheckedChange={(checked) => {
                                    if (checked) {
                                      setSelectedMembers([...selectedMembers, member.id]);
                                    } else {
                                      setSelectedMembers(selectedMembers.filter(id => id !== member.id));
                                    }
                                  }}
                                />
                                <Label htmlFor={`member-${member.id}`} className="text-sm cursor-pointer">
                                  {member.full_name}
                                </Label>
                              </div>
                            ))}
                          </div>
                        </div>
                        <div className="flex justify-end gap-2">
                          <DialogClose asChild>
                            <Button type="button" variant="outline">Cancel</Button>
                          </DialogClose>
                          <Button type="submit">Create Meeting</Button>
                        </div>
                      </form>
                    </DialogContent>
                  </Dialog>
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {meetings.length === 0 ? (
                    <p className="text-sm text-muted-foreground">No meetings scheduled</p>
                  ) : (
                    meetings.map((meeting) => (
                      <div key={meeting.id} className="p-4 border rounded-lg">
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <h3 className="font-semibold">{meeting.title}</h3>
                            <p className="text-sm text-muted-foreground">
                              {meeting.meeting_type || meeting.custom_meeting_type}
                            </p>
                            <p className="text-sm">
                              {new Date(meeting.meeting_date).toLocaleString()}
                            </p>
                            {meeting.location && (
                              <p className="text-sm text-muted-foreground">{meeting.location}</p>
                            )}
                            <p className="text-xs text-muted-foreground mt-1">
                              Status: {meeting.status}
                            </p>
                          </div>
                          <div className="flex gap-2">
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => {
                                setSelectedMeeting(meeting);
                                setIsMeetingDialogOpen(true);
                              }}
                            >
                              <Pencil className="h-3 w-3 mr-1" />
                              Edit
                            </Button>
                            {meeting.status === 'taken-place' && (
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => handleArchiveMeeting(meeting.id)}
                              >
                                <Archive className="h-3 w-3 mr-1" />
                                Archive
                              </Button>
                            )}
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={async () => {
                                if (confirm('Are you sure you want to delete this meeting?')) {
                                  const { error } = await supabase
                                    .from('meetings')
                                    .delete()
                                    .eq('id', meeting.id);
                                  
                                  if (error) {
                                    toast({
                                      title: "Error",
                                      description: error.message,
                                      variant: "destructive",
                                    });
                                  } else {
                                    toast({
                                      title: "Meeting deleted",
                                      description: "The meeting has been removed.",
                                    });
                                    fetchMeetings();
                                  }
                                }
                              }}
                            >
                              <Trash2 className="h-3 w-3 text-destructive" />
                            </Button>
                          </div>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Tasks Tab */}
          <TabsContent value="tasks" className="space-y-3">
            <Card>
              <CardHeader>
                <CardTitle>Tasks</CardTitle>
                <CardDescription>Manage personal, assigned, and general tasks</CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleAddTask} className="space-y-3 mb-6">
                  <div className="flex gap-2">
                    <Input
                      placeholder="Task title..."
                      value={newTaskTitle}
                      onChange={(e) => setNewTaskTitle(e.target.value)}
                      className="flex-1"
                      required
                    />
                    <Input
                      placeholder="Description (optional)..."
                      value={newTaskDescription}
                      onChange={(e) => setNewTaskDescription(e.target.value)}
                      className="flex-1"
                    />
                  </div>
                  <div className="flex gap-2 items-end">
                    <div className="flex-1 space-y-2">
                      <Label>Task Type</Label>
                      <Select value={taskType} onValueChange={(val: 'general' | 'assigned') => setTaskType(val)}>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="general">General (visible to all)</SelectItem>
                          <SelectItem value="assigned">Assigned (specific members)</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    {taskType === 'assigned' && (
                      <div className="flex-1 space-y-2">
                        <Label>Assign To</Label>
                        <div className="border rounded p-2 max-h-32 overflow-y-auto">
                          <div className="flex items-center space-x-2 mb-2">
                            <Checkbox
                              id="assign-me"
                              checked={taskAssignees.includes('me')}
                              onCheckedChange={(checked) => {
                                if (checked) {
                                  setTaskAssignees([...taskAssignees, 'me']);
                                } else {
                                  setTaskAssignees(taskAssignees.filter(id => id !== 'me'));
                                }
                              }}
                            />
                            <Label htmlFor="assign-me" className="text-sm cursor-pointer">ME</Label>
                          </div>
                          {members.map((member) => (
                            <div key={member.id} className="flex items-center space-x-2 mb-1">
                              <Checkbox
                                id={`assign-${member.id}`}
                                checked={taskAssignees.includes(member.id)}
                                onCheckedChange={(checked) => {
                                  if (checked) {
                                    setTaskAssignees([...taskAssignees, member.id]);
                                  } else {
                                    setTaskAssignees(taskAssignees.filter(id => id !== member.id));
                                  }
                                }}
                              />
                              <Label htmlFor={`assign-${member.id}`} className="text-sm cursor-pointer">
                                {member.full_name}
                              </Label>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                    <Button type="submit">
                      <Plus className="h-4 w-4 mr-2" />
                      Add Task
                    </Button>
                  </div>
                </form>

                <div className="space-y-4">
                  <div>
                    <h3 className="font-medium mb-2">Personal & Assigned Tasks</h3>
                    <div className="space-y-2">
                      {personalTasks.length === 0 ? (
                        <p className="text-sm text-muted-foreground">No tasks yet</p>
                      ) : (
                        personalTasks.map((task) => (
                          <div key={task.id} className="flex items-center gap-2 p-3 border rounded-lg">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleToggleTask(task.id, task.status)}
                            >
                              {task.status === 'completed' ? (
                                <CheckCircle2 className="h-5 w-5 text-green-600" />
                              ) : (
                                <Circle className="h-5 w-5" />
                              )}
                            </Button>
                            <div className="flex-1">
                              <p className={`font-medium ${task.status === 'completed' ? 'line-through text-muted-foreground' : ''}`}>
                                {task.title}
                              </p>
                              {task.description && (
                                <p className="text-sm text-muted-foreground">{task.description}</p>
                              )}
                              {task.task_type === 'assigned' && (
                                <p className="text-xs text-muted-foreground mt-1">Assigned task</p>
                              )}
                            </div>
                            {task.task_type === 'personal' && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleDeleteTask(task.id)}
                              >
                                <Trash2 className="h-4 w-4 text-destructive" />
                              </Button>
                            )}
                          </div>
                        ))
                      )}
                    </div>
                  </div>

                  <div>
                    <h3 className="font-medium mb-2">General Tasks (Visible to All)</h3>
                    <div className="space-y-2">
                      {generalTasks.length === 0 ? (
                        <p className="text-sm text-muted-foreground">No general tasks</p>
                      ) : (
                        generalTasks.map((task) => (
                          <div key={task.id} className="p-3 border rounded-lg bg-muted/30">
                            <p className="font-medium">{task.title}</p>
                            {task.description && (
                              <p className="text-sm text-muted-foreground">{task.description}</p>
                            )}
                          </div>
                        ))
                      )}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Communications Tab */}
          <TabsContent value="communications" className="space-y-3">
            <Card>
              <CardHeader>
                <CardTitle>Communications</CardTitle>
                <CardDescription>Send messages to committee members</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label>Recipients</Label>
                    <div className="border rounded p-3 max-h-48 overflow-y-auto">
                      {members.map((member) => (
                        <div key={member.id} className="flex items-center space-x-2 mb-2">
                          <Checkbox
                            id={`comm-${member.id}`}
                            checked={commRecipients.includes(member.id)}
                            onCheckedChange={(checked) => {
                              if (checked) {
                                setCommRecipients([...commRecipients, member.id]);
                              } else {
                                setCommRecipients(commRecipients.filter(id => id !== member.id));
                              }
                            }}
                          />
                          <Label htmlFor={`comm-${member.id}`} className="cursor-pointer">
                            {member.full_name} ({member.email})
                          </Label>
                        </div>
                      ))}
                    </div>
                    {commRecipients.length > 0 && (
                      <p className="text-sm text-muted-foreground">{commRecipients.length} member(s) selected</p>
                    )}
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="comm-message">Message</Label>
                    <Textarea
                      id="comm-message"
                      value={commMessage}
                      onChange={(e) => setCommMessage(e.target.value)}
                      placeholder="Type your message here..."
                      rows={6}
                    />
                  </div>
                  <Button onClick={handleSendCommunication} className="w-full">
                    <Send className="h-4 w-4 mr-2" />
                    Send Communication
                  </Button>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Alerts Tab */}
          <TabsContent value="alerts" className="space-y-3">
            <Card>
              <CardHeader>
                <CardTitle>Alerts</CardTitle>
                <CardDescription>Important alerts and reminders</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {alerts.length === 0 ? (
                    <p className="text-sm text-muted-foreground">No alerts</p>
                  ) : (
                    alerts.map((alert) => {
                      const daysUntilDue = Math.ceil((new Date(alert.due_date).getTime() - new Date().getTime()) / (1000 * 60 * 60 * 24));
                      const isCritical = daysUntilDue <= 7 && daysUntilDue >= 0;
                      
                      return (
                        <div key={alert.id} className={`p-3 border rounded-lg ${isCritical ? 'border-destructive bg-destructive/5' : ''}`}>
                          <div className="flex items-start justify-between">
                            <div>
                              <p className="font-medium">{alert.title}</p>
                              <p className="text-sm text-muted-foreground">Due: {new Date(alert.due_date).toLocaleDateString()}</p>
                              <p className={`text-sm ${isCritical ? 'text-destructive font-medium' : 'text-muted-foreground'}`}>
                                {daysUntilDue > 0 ? `${daysUntilDue} days remaining` : 'Overdue'}
                              </p>
                            </div>
                          </div>
                        </div>
                      );
                    })
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
